import React, { useEffect, useState } from 'react';
import { ImagePlusIcon, MailIcon, PoundSterlingIcon, XIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { AppUser, USER_ROLES, UserRole } from '../../data/settingsStore';
type UserDrawerProps = {
  user?: AppUser;
  onClose: () => void;
  onSave: (user: Omit<AppUser, 'id' | 'status'>) => void;
};
export function UserDrawer({
  user,
  onClose,
  onSave
}: UserDrawerProps) {
  const isEdit = Boolean(user);
  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [email, setEmail] = useState(user?.email || '');
  const [role, setRole] = useState<UserRole | ''>(user?.role || '');
  const [hourlyCost, setHourlyCost] = useState(user?.hourlyCost?.toString() || '');
  const [profileUrl, setProfileUrl] = useState(user?.profileUrl || '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};
    if (!firstName.trim()) nextErrors.firstName = 'First name is required.';
    if (!lastName.trim()) nextErrors.lastName = 'Last name is required.';
    if (!email.trim()) nextErrors.email = 'Email address is required.';else if (!/^\S+@\S+\.\S+$/.test(email)) nextErrors.email = 'Enter a valid email address.';
    if (!role) nextErrors.role = 'Select a role.';
    if (!hourlyCost || Number.isNaN(Number(hourlyCost)) || Number(hourlyCost) < 0) {
      nextErrors.hourlyCost = 'Enter a valid hourly cost.';
    }
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    onSave({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      role,
      hourlyCost: Number(hourlyCost),
      profileUrl: profileUrl || undefined
    });
  };
  return <div className="fixed inset-0 z-[100] flex justify-end" role="presentation" data-id="element-317" data-mp="2mrihn8yl3l9j">
      <button type="button" onClick={onClose} className="absolute inset-0 cursor-default bg-slate-900/40 backdrop-blur-sm" aria-label="Close user form" data-id="element-318" data-mp="2hv0bc4nlvev3" />
      <motion.aside initial={{
      x: '100%'
    }} animate={{
      x: 0
    }} exit={{
      x: '100%'
    }} transition={{
      type: 'tween',
      duration: 0.25,
      ease: [0.32, 0.72, 0, 1]
    }} className="relative flex h-full w-full max-w-xl flex-col bg-white shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="user-drawer-title" data-id="element-319" data-mp="3ahl4nann611a">
        <div className="flex items-start justify-between border-b border-slate-100 p-6" data-id="element-320" data-mp="1l3zh7bstcbgj">
          <div data-id="element-321" data-mp="lnofxhlu1p3">
            <h2 id="user-drawer-title" className="text-xl font-bold text-slate-900" data-id="element-322" data-mp="m757aviqk1j">
              {isEdit ? 'Edit user' : 'New user'}
            </h2>
            <p className="mt-1 text-sm text-slate-500" data-id="element-323" data-mp="9mlrodk7y56m">
              {isEdit ? 'Update this user’s account details.' : 'Invite a colleague to join the platform.'}
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600" aria-label="Close user form" data-id="element-324" data-mp="jb3q4o510073">
            <XIcon className="h-5 w-5" data-id="element-325" data-mp="t88g6z1zsft6" />
          </button>
        </div>

        <form onSubmit={submit} className="flex flex-1 flex-col overflow-y-auto" data-id="element-326" data-mp="xbegzr6c5es9">
          <div className="space-y-5 p-6" data-id="element-327" data-mp="2v3ion1e216o0">
            <label className="block text-sm font-medium text-slate-700" data-id="element-328" data-mp="2z31embkqg7xp">
              Profile picture <span className="text-slate-400" data-id="element-329" data-mp="23suq9osf7fmu">(optional)</span>
              <span className="mt-2 flex items-center gap-3" data-id="element-330" data-mp="23rbb61ii3amn">
                <span className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-full bg-slate-100 text-sm font-semibold text-slate-500" data-id="element-331" data-mp="1zsawmv722u5g">
                  {profileUrl ? <img src={profileUrl} alt="Selected profile preview" className="h-full w-full object-cover" data-id="element-332" data-mp="28rxyfi3p61z1" /> : `${firstName.slice(0, 1)}${lastName.slice(0, 1)}`.toUpperCase() || 'U'}
                </span>
                <span className="relative inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50" data-id="element-333" data-mp="1q1zpkzs2s4uc">
                  <ImagePlusIcon className="h-4 w-4" data-id="element-334" data-mp="xdk9sb14dihy" />
                  Upload picture
                  <input type="file" accept="image/*" className="absolute inset-0 cursor-pointer opacity-0" onChange={event => {
                  const selected = event.target.files?.[0];
                  if (selected) setProfileUrl(URL.createObjectURL(selected));
                }} data-id="element-335" data-mp="390nudsd56nce" />
                </span>
              </span>
            </label>

            <div className="grid gap-4 sm:grid-cols-2" data-id="element-336" data-mp="3rrovalxtosyl">
              <Field label="First name" error={errors.firstName} required data-id="element-337" data-mp="kuffhygljxg">
                <input value={firstName} onChange={event => setFirstName(event.target.value)} className={inputClass(Boolean(errors.firstName))} autoComplete="given-name" data-id="element-338" data-mp="3iqh5ks9dh0g4" />
              </Field>
              <Field label="Last name" error={errors.lastName} required data-id="element-339" data-mp="27deozx79wavy">
                <input value={lastName} onChange={event => setLastName(event.target.value)} className={inputClass(Boolean(errors.lastName))} autoComplete="family-name" data-id="element-340" data-mp="2cbg7oh0se86w" />
              </Field>
              <Field label="Email address" error={errors.email} required className="sm:col-span-2" data-id="element-341" data-mp="82sjh6xvg2ta">
                <div className="relative" data-id="element-342" data-mp="3v931140xe26p">
                  <MailIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" data-id="element-343" data-mp="3v6qai2h3qji1" />
                  <input type="email" value={email} onChange={event => setEmail(event.target.value)} className={`${inputClass(Boolean(errors.email))} pl-9`} autoComplete="email" data-id="element-344" data-mp="3lanbv6lgl2zu" />
                </div>
              </Field>
              <Field label="Role" error={errors.role} required data-id="element-345" data-mp="1fj01r73iw8pz">
                <select value={role} onChange={event => {
                setRole(event.target.value as UserRole);
                setErrors(current => {
                  if (!current.role) return current;
                  const nextErrors = {
                    ...current
                  };
                  delete nextErrors.role;
                  return nextErrors;
                });
              }} className={inputClass(Boolean(errors.role))} aria-invalid={Boolean(errors.role)} data-id="element-346" data-mp="2xfjtsb30fdv7">
                  <option value="" disabled data-id="element-347" data-mp="2jhdgyitbceo1">
                    Select a role
                  </option>
                  {USER_ROLES.map(option => <option key={option} value={option} data-id="element-348" data-mp="2sh09ef42ea4y">
                      {option}
                    </option>)}
                </select>
              </Field>
              <Field label="Hourly cost" error={errors.hourlyCost} required data-id="element-349" data-mp="7p6s7ol41xe3">
                <div className="relative" data-id="element-350" data-mp="3fik7nd0j4he6">
                  <PoundSterlingIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" data-id="element-351" data-mp="3fjlibgdmv1vd" />
                  <input type="number" min="0" step="0.01" inputMode="decimal" value={hourlyCost} onChange={event => {
                  setHourlyCost(event.target.value);
                  setErrors(current => {
                    if (!current.hourlyCost) return current;
                    const nextErrors = {
                      ...current
                    };
                    delete nextErrors.hourlyCost;
                    return nextErrors;
                  });
                }} placeholder="0.00" className={`${inputClass(Boolean(errors.hourlyCost))} pl-9`} aria-invalid={Boolean(errors.hourlyCost)} data-id="element-352" data-mp="3ahw8u5626w9e" />
                </div>
                <span className="mt-1 block text-xs font-normal text-slate-500" data-id="element-353" data-mp="1ypwz5xhbd3lk">
                  Used to value future time logged against jobs.
                </span>
              </Field>
            </div>
          </div>
          <div className="mt-auto flex items-center justify-end gap-3 border-t border-slate-100 bg-slate-50 p-6" data-id="element-354" data-mp="1ozu5dv2dblm6">
            <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50" data-id="element-355" data-mp="1ty5g080hm05g">
              Cancel
            </button>
            <button type="submit" className="rounded-lg bg-[#6A9A24] px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[#587F1D]" data-id="element-356" data-mp="162j3s3t9kg3k">
              {isEdit ? 'Save changes' : 'Invite user'}
            </button>
          </div>
        </form>
      </motion.aside>
    </div>;
}
function Field({
  label,
  error,
  required,
  className = '',
  children
}: {
  label: string;
  error?: string;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return <label className={`block text-sm font-medium text-slate-700 ${className}`} data-id="element-357" data-mp="2komhjw3d5ohp">
      {label} {required && <span className="text-rose-600" data-id="element-358" data-mp="26t1z11rjr8at">*</span>}
      <span className="mt-1.5 block" data-id="element-359" data-mp="2avymb5s17sl3">{children}</span>
      {error && <span className="mt-1 block text-xs font-normal text-rose-600" data-id="element-360" data-mp="2ymkgbt57bcc3">
          {error}
        </span>}
    </label>;
}
function inputClass(hasError: boolean) {
  return `w-full rounded-lg border bg-white px-3 py-2.5 text-sm text-slate-700 outline-none focus:ring-2 ${hasError ? 'border-rose-300 focus:border-rose-400 focus:ring-rose-100' : 'border-slate-200 focus:border-blue-400 focus:ring-blue-100'}`;
}